import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Skeleton,
  Paper,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import boardServices from "../../services/boardServices";
import ImageWithFallback from "../../components/ImageWithFallback";

// Format as "15 February, 2025"
const formatBoardDate = (date) => {
  if (!date) return "—";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "—";
  const day = d.getDate();
  const month = d.toLocaleDateString("en-US", { month: "long" });
  const year = d.getFullYear();
  return `${day} ${month}, ${year}`;
};

const getStartDate = (member) => {
  if (!member) return null;
  if (member.effectiveStartDate || member.startDate || member.createdAt)
    return member.effectiveStartDate || member.startDate || member.createdAt;
  const id = member._id;
  if (!id) return null;
  const hex = typeof id === "string" ? id : (id?.toString && id.toString());
  if (!hex || hex.length < 8) return null;
  const sec = parseInt(hex.slice(0, 8), 16);
  if (Number.isNaN(sec)) return null;
  return new Date(sec * 1000).toISOString();
};

const BoardMemberDetail = () => {
  const { id } = useParams();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    boardServices
      .getByID(id)
      .then((res) => setMember(res?.data?.data))
      .catch(() => setMember(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ py: 6, backgroundColor: "#f8f8f8", minHeight: "60vh" }}>
        <Container maxWidth="lg">
          <Skeleton height={40} width={160} />
          <Box sx={{ display: "flex", gap: 4, mt: 4, flexWrap: "wrap" }}>
            <Skeleton variant="rectangular" width={320} height={380} sx={{ borderRadius: 2 }} />
            <Box sx={{ flex: 1, minWidth: 280 }}>
              <Skeleton height={48} width="60%" />
              <Skeleton height={32} sx={{ mt: 2 }} />
              <Skeleton height={28} sx={{ mt: 1 }} />
              <Skeleton height={120} sx={{ mt: 3 }} />
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  if (!member) {
    return (
      <Box sx={{ py: 6, textAlign: "center", backgroundColor: "#f8f8f8", minHeight: "60vh" }}>
        <Container maxWidth="lg">
          <Typography variant="h6" color="text.secondary">
            Board member not found.
          </Typography>
          <Button
            component={Link}
            to="/board-members"
            startIcon={<ArrowBackIcon />}
            sx={{ mt: 2 }}
          >
            Back to Board Members
          </Button>
        </Container>
      </Box>
    );
  }

  const startDate = getStartDate(member);
  const endDate = member.endDate;
  const timeframe = endDate
    ? `${formatBoardDate(startDate)} – ${formatBoardDate(endDate)}`
    : `${formatBoardDate(startDate)} – Current`;

  return (
    <Box sx={{ py: 6, backgroundColor: "#f8f8f8", minHeight: "60vh" }}>
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 2 } }}>
        <Button
          component={Link}
          to="/board-members"
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 3 }}
        >
          Back to Board Members
        </Button>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 4 },
            borderRadius: 2,
            backgroundColor: "#fff",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 4,
              alignItems: { md: "flex-start" },
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                width: { xs: "100%", md: 320 },
                maxWidth: "100%",
              }}
            >
            <ImageWithFallback
              path={member.image}
              alt={member.name}
              style={{
                width: "100%",
                maxWidth: 320,
                height: "auto",
                borderRadius: 8,
                objectFit: "cover",
                display: "block",
              }}
            />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#1a80c8", mb: 1 }}>
                {member.name}
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                {member.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {timeframe}
              </Typography>
              {member.description && (
                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: "pre-wrap",
                    color: "#333",
                    lineHeight: 1.7,
                  }}
                >
                  {member.description}
                </Typography>
              )}
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default BoardMemberDetail;
